import { BookOpen, School, TriangleAlert } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router';
import { Logo } from '../components/Layout';
import { SignInWelcome } from '../components/sign-in-welcome';
import { Alert, Button, EmptyState, Field, Input, Spinner } from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth, useAuthOptions } from '../lib/auth';
import { usePublicSchool } from '../lib/branding';
import { currentSchool } from '../lib/school';

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="size-5" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/** The school's branded sign-in page at /{school}/login. */
export function SchoolLoginPage() {
  const { me, login } = useAuth();
  const school = usePublicSchool();
  const options = useAuthOptions();
  const location = useLocation();
  const [params] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const callbackError = params.get('error');

  if (me?.tenant) return <Navigate to={from} replace />;

  if (school.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-7" />
      </div>
    );
  }
  if (school.error || !school.data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <EmptyState
          icon={<School />}
          title="School not found"
          description={`There’s no school at /${currentSchool}. Check the address your technology team shared.`}
          action={
            <a href="/login" className="text-sm font-medium text-brand-700 hover:underline">
              Go to Edvora sign-in
            </a>
          }
        />
      </div>
    );
  }

  const info = school.data;
  // Staff support links: Edvora admins may use a password even where the school turned them off.
  const passwordAvailable = info.signIn.password || showPassword;
  const googleHref = `/api/auth/google/start?${new URLSearchParams({ school: info.slug, returnTo: from })}`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <SignInWelcome
        className="hidden lg:block"
        name={info.name}
        logoUrl={info.logoUrl}
        imageUrl={info.signInImageUrl}
        headline={info.signInHeadline}
        message={info.signInMessage}
      />

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            {info.logoUrl ? <img src={info.logoUrl} alt="" className="size-10 rounded-lg object-contain" /> : <Logo />}
            <span className="font-semibold">{info.name}</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">
            {info.signIn.google ? `Use your ${info.name} Google account.` : `Sign in to ${info.name}.`}
          </p>

          <div className="mt-8 space-y-5">
            {info.suspended && <Alert tone="amber">{info.name}’s Edvora account is suspended. Contact your technology team.</Alert>}
            {(callbackError || error) && (
              <Alert tone="red" icon={<TriangleAlert />}>
                {error || callbackError}
              </Alert>
            )}
            {me && !me.tenant && !error && !callbackError && (
              <Alert tone="amber">
                You’re signed in as {me.user.email}, which can’t open {info.name}
                {info.signIn.google ? ' with a password. Sign in with Google instead.' : '.'}
              </Alert>
            )}

            {info.signIn.google && (
              <a
                href={googleHref}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:bg-slate-50"
              >
                <GoogleMark />
                Sign in with Google
              </a>
            )}

            {!info.signIn.google && !info.signIn.password && (
              <Alert tone="amber">Sign-in isn’t set up for {info.name} yet. Contact your technology team.</Alert>
            )}

            {passwordAvailable && (
              <form onSubmit={submit} className="space-y-5">
                {info.signIn.google && (
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
                  </div>
                )}
                <Field label="Email">
                  <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <Field
                  label="Password"
                  hint={
                    options.data?.passwordReset && (
                      // Edvora-wide page, outside this school's router.
                      <a href={`/reset-password?school=${encodeURIComponent(info.slug)}`} className="font-medium text-brand-700 hover:underline">
                        Forgot your password?
                      </a>
                    )
                  }
                >
                  <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                </Field>
                <Button type="submit" variant={info.signIn.google ? 'secondary' : 'primary'} className="w-full" loading={submitting}>
                  Sign in with email
                </Button>
              </form>
            )}
          </div>

          {info.knowledgebase && (
            <div className="mt-10 border-t border-slate-200 pt-6 text-center">
              <p className="text-xs text-slate-500">Looking for help? Browse answers without signing in.</p>
              <Link
                to="/help"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
              >
                <BookOpen className="size-4 text-brand-700" />
                Knowledgebase
              </Link>
            </div>
          )}

          <p className="mt-10 text-center text-xs text-slate-400">
            {!info.signIn.password && !showPassword ? (
              <button type="button" className="hover:text-slate-600" onClick={() => setShowPassword(true)}>
                Edvora support sign-in
              </button>
            ) : (
              // Outside this school's pages, so a plain link rather than the router's.
              <a href="/login" className="hover:text-slate-600">
                Not {info.name}? Find your school
              </a>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
